import { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { DataTable } from '@/components/ui/data-table';
import type { Column } from '@/components/ui/data-table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ArrowLeft,
  ArrowRight,
  Ban,
  Code2,
  Database,
  Eye,
  Files,
  RotateCcw,
  Loader2,
  CheckCircle,
  Search,
  Table2,
  Undo2,
  XCircle,
  AlertTriangle,
  Clock,
  Sparkles,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { cn, formatPercent } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { migrationV1V2Api } from './api/migration-v1-v2';
import { StepStatusBadge } from './components/StepStatusBadge';
import { RecordStatusBadge } from './components/RecordStatusBadge';
import { RecordDetailDialog } from './components/RecordDetailDialog';
import { StepFilesPanel } from './components/StepFilesPanel';
import {
  fieldLabel,
  formatValue,
  mappingFields,
  readableColumns,
  recordStatusOptions,
  recordTitle,
} from './components/lotPresentation';
import type { MigrationStepExecution, MigrationRecord, RecordStatus } from './types';

/** Lots « fichiers » : exposent l'onglet de comparaison V1/V2 des binaires. */
const FILE_LOTS = new Set([
  'users.photos',
  'settings.logo',
  'messaging.attachments',
  'ged.documents',
  'students.suivi',
  'students.suivi_files',
  'students.formulaires',
  'students.inscriptions',
  'documents.factures',
  'documents.bulletins',
  'montessori.suivi_photos',
]);

interface StatCardSmallProps {
  title: string;
  value: number;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  /** Rend la carte cliquable : filtre la liste sur le statut correspondant. */
  onClick?: () => void;
  /** Le filtre correspondant est actif (re-clic = désactivation). */
  active?: boolean;
}

function StatCardSmall({ title, value, icon: Icon, color, onClick, active }: StatCardSmallProps) {
  const Wrapper = onClick ? 'button' : 'div';
  return (
    <Wrapper
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      title={onClick ? (active ? 'Retirer le filtre' : `Filtrer : ${title.toLowerCase()}`) : undefined}
      className={cn(
        'bg-white dark:bg-slate-800/50 rounded-xl border shadow-sm p-4 flex items-center gap-3 text-left',
        active ? 'border-primary ring-1 ring-primary' : 'border-gray-100 dark:border-slate-700',
        onClick && 'cursor-pointer transition-colors hover:border-primary/60'
      )}
    >
      <div className={cn('p-2 rounded-lg', color)}>
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{value}</p>
        <p className="text-xs text-gray-500 dark:text-gray-400">{title}</p>
      </div>
    </Wrapper>
  );
}

type ViewTab = 'data' | 'files' | 'technical';

/**
 * Visualiseur d'un lot de migration.
 *
 * Première lecture (onglet « Données ») : tableau lisible par tous — un
 * enregistrement par ligne avec ses données en clair (prénom, nom, date de
 * naissance…) et sa correspondance V1 → V2. Le clic ouvre le détail champ à
 * champ. Onglet « Fichiers » (lots photos / GED / PJ) : les fichiers migrés
 * des deux côtés. Seconde lecture (onglet « Technique ») : mapping, requête
 * source, suggestions IA, JSON bruts.
 */
export function StepDetailPage() {
  const { id, stepId } = useParams<{ id: string; stepId: string }>();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canExecute = hasPermission('action.migration_v1_v2.execute');
  const projectId = Number(id);
  const stepExecutionId = Number(stepId);

  const [step, setStep] = useState<MigrationStepExecution | null>(null);
  const [records, setRecords] = useState<MigrationRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [error, setError] = useState('');
  const [recordsPage, setRecordsPage] = useState(1);
  const [recordsTotalItems, setRecordsTotalItems] = useState(0);
  const [recordsTotalPages, setRecordsTotalPages] = useState(0);
  const [statusFilter, setStatusFilter] = useState<RecordStatus | ''>('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [selectedRecord, setSelectedRecord] = useState<MigrationRecord | null>(null);
  const [retrying, setRetrying] = useState<number | null>(null);
  const [tab, setTab] = useState<ViewTab>('data');
  // Exclusion manuelle : record ciblé + raison saisie (dialog dédié).
  const [excludeTarget, setExcludeTarget] = useState<MigrationRecord | null>(null);
  const [excludeReason, setExcludeReason] = useState('');
  const [excluding, setExcluding] = useState(false);
  const [excludeError, setExcludeError] = useState<string | null>(null);
  const [including, setIncluding] = useState<number | null>(null);

  const fetchStep = useCallback(async () => {
    try {
      setLoading(true);
      const data = await migrationV1V2Api.getStepExecution(stepExecutionId);
      setStep(data);
      // Lots « fichiers » (photos, GED, PJ…) : l'onglet Fichiers est la vue la
      // plus parlante — on l'ouvre par défaut. Fait au chargement initial
      // uniquement (les refresh silencieux ne réinitialisent pas l'onglet).
      if (FILE_LOTS.has(data.stepDefinition.code)) {
        setTab('files');
      }
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } }).response?.status;
      setError(
        status === 403
          ? "Vous n'avez pas accès à ce dossier de migration : il est réservé au responsable assigné et aux administrateurs."
          : 'Étape non trouvée'
      );
    } finally {
      setLoading(false);
    }
  }, [stepExecutionId]);

  const fetchRecords = useCallback(async () => {
    try {
      setRecordsLoading(true);
      const response = await migrationV1V2Api.getRecords(stepExecutionId, {
        status: statusFilter || undefined,
        q: search || undefined,
        page: recordsPage,
        limit: 25,
      });
      setRecords(response.data);
      setRecordsTotalItems(response.meta.total);
      setRecordsTotalPages(response.meta.totalPages);
    } catch {
      // silent
    } finally {
      setRecordsLoading(false);
    }
  }, [stepExecutionId, statusFilter, search, recordsPage]);

  useEffect(() => {
    fetchStep();
  }, [fetchStep]);

  useEffect(() => {
    if (step) fetchRecords();
  }, [step, fetchRecords]);

  // Debounce de la recherche (400 ms).
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setRecordsPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Rafraîchit le step SANS passer par le skeleton (compteurs/statut du lot
  // après une relance unitaire) ; le changement de `step` refetch les records.
  const refreshStepSilent = useCallback(async () => {
    try {
      setStep(await migrationV1V2Api.getStepExecution(stepExecutionId));
    } catch {
      // silent
    }
  }, [stepExecutionId]);

  // Relance unitaire SYNCHRONE : le backend rejoue le loader sur ce record
  // et renvoie son état final.
  const handleRetry = async (recordId: number) => {
    try {
      setRetrying(recordId);
      await migrationV1V2Api.retryRecord(recordId);
      await refreshStepSilent();
    } catch {
      // silent
    } finally {
      setRetrying(null);
    }
  };

  // Exclusion manuelle (raison obligatoire — reprise dans le rapport).
  const handleExcludeConfirm = async () => {
    if (!excludeTarget || excludeReason.trim() === '') return;
    try {
      setExcluding(true);
      setExcludeError(null);
      await migrationV1V2Api.excludeRecord(excludeTarget.id, excludeReason.trim());
      setExcludeTarget(null);
      setExcludeReason('');
      await refreshStepSilent();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } }; message?: string };
      setExcludeError(err.response?.data?.error ?? err.message ?? String(e));
    } finally {
      setExcluding(false);
    }
  };

  // Ré-inclusion : le record repasse « en attente » et sera repris.
  const handleInclude = async (recordId: number) => {
    try {
      setIncluding(recordId);
      await migrationV1V2Api.includeRecord(recordId);
      await refreshStepSilent();
    } catch {
      // silent
    } finally {
      setIncluding(null);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-4 w-full" />
        <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-20 rounded-xl" />
          ))}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (error || !step) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => navigate(`/migration-v1-v2/${projectId}`)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour au projet
        </Button>
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">
          {error || 'Étape non trouvée'}
        </div>
      </div>
    );
  }

  const isFileLot = FILE_LOTS.has(step.stepDefinition.code);
  const dynamicColumns = readableColumns(step.fieldMapping, 3);

  // Filtre statut partagé entre les onglets Données et Fichiers, pilotable
  // depuis les selects OU d'un clic sur une carte de stats (ex. « Échoués »
  // pour cibler tout ce qui est en erreur). Re-clic sur la carte active =
  // retour à « tous les statuts ».
  const applyStatusFilter = (status: RecordStatus | '') => {
    setStatusFilter((current) => (current === status ? '' : status));
    setRecordsPage(1);
    if (tab === 'technical') setTab(isFileLot ? 'files' : 'data');
  };

  const columns: Column<MigrationRecord>[] = [
    {
      key: 'title',
      header: 'Élément',
      className: 'w-72',
      render: (r) => (
        <button
          onClick={() => setSelectedRecord(r)}
          title={recordTitle(r)}
          className="block max-w-full truncate text-left font-medium text-gray-900 dark:text-gray-100 hover:text-primary hover:underline"
        >
          {recordTitle(r)}
        </button>
      ),
    },
    ...dynamicColumns.map((col) => ({
      key: col.key,
      header: col.label,
      className: 'w-40',
      render: (r: MigrationRecord) => (
        <span className="block truncate text-sm text-gray-600 dark:text-gray-400">
          {formatValue((r.mappedData as Record<string, unknown> | null)?.[col.key])}
        </span>
      ),
    })),
    {
      key: 'correspondence',
      header: 'V1 → V2',
      className: 'w-44',
      render: (r) => (
        <span className="inline-flex items-center gap-1.5 text-xs">
          <span className="font-mono text-gray-500">#{r.sourceId}</span>
          <ArrowRight className="h-3 w-3 text-gray-300 shrink-0" />
          {r.targetId ? (
            <span
              className="font-mono text-green-600 truncate max-w-[7rem] inline-block align-bottom"
              title={r.targetId}
            >
              {r.targetId.slice(0, 8)}…
            </span>
          ) : (
            <span className="text-gray-400">—</span>
          )}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Statut',
      className: 'w-32',
      render: (r) => (
        <div className="space-y-0.5">
          <RecordStatusBadge status={r.status} />
          {r.errorMessage && (
            <p
              className={cn(
                'text-[11px] truncate max-w-[14rem]',
                r.status === 'excluded'
                  ? 'text-gray-500'
                  : r.status === 'migrated'
                    ? 'text-amber-600 dark:text-amber-500'
                    : 'text-red-600'
              )}
              title={r.errorMessage}
            >
              {r.errorMessage.slice(0, 60)}
              {r.errorMessage.length > 60 ? '…' : ''}
            </p>
          )}
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right w-20',
      render: (r) => (
        <div className="flex items-center justify-end gap-1">
          {r.status === 'failed' && canExecute && (
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => handleRetry(r.id)}
              disabled={retrying === r.id}
              title="Réessayer"
            >
              {retrying === r.id ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RotateCcw className="h-4 w-4 text-orange-500" />
              )}
            </Button>
          )}
          {r.status !== 'migrated' && r.status !== 'excluded' && canExecute && (
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => {
                setExcludeReason('');
                setExcludeError(null);
                setExcludeTarget(r);
              }}
              title="Exclure de la migration"
            >
              <Ban className="h-4 w-4 text-gray-400 hover:text-gray-600" />
            </Button>
          )}
          {r.status === 'excluded' && canExecute && (
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => handleInclude(r.id)}
              disabled={including === r.id}
              title="Ré-inclure dans la migration"
            >
              {including === r.id ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Undo2 className="h-4 w-4 text-blue-500" />
              )}
            </Button>
          )}
          <Button variant="ghost" size="icon-sm" onClick={() => setSelectedRecord(r)} title="Détail">
            <Eye className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  const tabs: { key: ViewTab; label: string; Icon: typeof Table2 }[] = [
    { key: 'data', label: 'Données', Icon: Table2 },
    ...(isFileLot ? [{ key: 'files' as ViewTab, label: 'Fichiers', Icon: Files }] : []),
    { key: 'technical', label: 'Technique', Icon: Code2 },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon-sm" asChild>
            <Link to={`/migration-v1-v2/${projectId}`}>
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {step.stepDefinition.name}
          </h1>
          <StepStatusBadge status={step.status} />
          {step.attempt > 1 && (
            <Badge variant="outline" className="text-xs">Tentative {step.attempt}</Badge>
          )}
        </div>
        {/* Progress bar */}
        <div className="space-y-1">
          <div className="flex items-center justify-between text-sm text-gray-500">
            <span>{step.processedRecords}/{step.totalRecords} enregistrements traités</span>
            <span>{formatPercent(step.progressPercent)}%</span>
          </div>
          <Progress value={step.progressPercent} className="h-3" />
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
        <StatCardSmall title="Total" value={step.totalRecords} icon={Database} color="bg-blue-100 text-blue-600" onClick={() => applyStatusFilter('')} />
        <StatCardSmall title="Traités" value={step.processedRecords} icon={Clock} color="bg-cyan-100 text-cyan-600" />
        <StatCardSmall title="Réussis" value={step.successRecords} icon={CheckCircle} color="bg-green-100 text-green-600" onClick={() => applyStatusFilter('migrated')} active={statusFilter === 'migrated'} />
        <StatCardSmall title="Échoués" value={step.failedRecords} icon={XCircle} color="bg-red-100 text-red-600" onClick={() => applyStatusFilter('failed')} active={statusFilter === 'failed'} />
        <StatCardSmall title="Exclus" value={step.excludedRecords} icon={Ban} color="bg-gray-100 text-gray-600" onClick={() => applyStatusFilter('excluded')} active={statusFilter === 'excluded'} />
        <StatCardSmall title="À vérifier" value={step.pendingReviewRecords} icon={AlertTriangle} color="bg-orange-100 text-orange-600" onClick={() => applyStatusFilter('review_needed')} active={statusFilter === 'review_needed'} />
      </div>

      {/* Onglets Données / Fichiers / Technique */}
      <div className="border-b border-gray-200 dark:border-slate-700 flex items-center gap-1">
        {tabs.map(({ key, label, Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={cn(
              'inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors',
              tab === key
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'
            )}
          >
            <Icon className="h-4 w-4" />
            {label}
          </button>
        ))}
      </div>

      {/* ------- Onglet Données (première lecture, lisible par tous) ------- */}
      {tab === 'data' && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="relative w-full sm:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 z-10" />
              <Input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Rechercher (nom, e-mail, id…)"
                className="pl-9"
              />
            </div>
            <Select
              value={statusFilter || 'all'}
              onValueChange={(val) => {
                setStatusFilter((val === 'all' ? '' : val) as RecordStatus | '');
                setRecordsPage(1);
              }}
            >
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Tous les statuts" />
              </SelectTrigger>
              <SelectContent>
                {recordStatusOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <DataTable
            columns={columns}
            data={records}
            loading={recordsLoading}
            page={recordsPage}
            pageSize={25}
            totalItems={recordsTotalItems}
            totalPages={recordsTotalPages}
            onPageChange={setRecordsPage}
            emptyMessage={search ? 'Aucun résultat pour cette recherche' : 'Aucun enregistrement'}
          />
        </div>
      )}

      {/* ------- Onglet Fichiers (lots photos / GED / PJ) ------- */}
      {tab === 'files' && isFileLot && (
        <StepFilesPanel
          stepExecutionId={step.id}
          statusFilter={statusFilter}
          onStatusFilterChange={(status) => {
            setStatusFilter(status);
            setRecordsPage(1);
          }}
          onCountersChanged={refreshStepSilent}
        />
      )}

      {/* ------- Onglet Technique (seconde lecture) ------- */}
      {tab === 'technical' && (
        <div className="space-y-6">
          {/* AI Mapping confidence */}
          {step.aiMappingConfidence !== null && step.aiMappingConfidence !== undefined && (
            <Card>
              <CardHeader className="border-b border-border bg-muted/30 py-3">
                <CardTitle className="flex items-center gap-2 text-sm">
                  <Sparkles className="h-4 w-4 text-violet-500" />
                  Mapping IA
                  <Badge variant={step.aiMappingConfidence >= 0.8 ? 'success' : 'warning'} className="text-xs">
                    Confiance : {Math.round(step.aiMappingConfidence * 100)}%
                  </Badge>
                </CardTitle>
              </CardHeader>
              {step.aiMappingSuggestions && (
                <CardContent className="pt-3">
                  <pre className="text-xs font-mono bg-gray-50 dark:bg-slate-900 rounded-lg p-3 overflow-auto max-h-48 border">
                    {JSON.stringify(step.aiMappingSuggestions, null, 2)}
                  </pre>
                </CardContent>
              )}
            </Card>
          )}

          {/* Mapping config */}
          {step.fieldMapping && (
            <Card>
              <CardHeader className="border-b border-border bg-muted/30 py-3">
                <CardTitle className="flex items-center gap-2 text-sm">
                  <ArrowRight className="h-4 w-4 text-primary" />
                  Configuration du mapping
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-3">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 pr-4 font-medium text-gray-500">Donnée</th>
                        <th className="text-left py-2 pr-4 font-medium text-gray-500">Champ source (V1)</th>
                        <th className="text-left py-2 pr-4 font-medium text-gray-500">Champ cible (V2)</th>
                        <th className="text-left py-2 pr-4 font-medium text-gray-500">Transformation</th>
                        <th className="text-left py-2 font-medium text-gray-500">Obligatoire</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mappingFields(step.fieldMapping).map(({ v2Field, v1Field, transform, required }) => (
                        <tr key={v2Field} className="border-b last:border-b-0">
                          <td className="py-2 pr-4 text-gray-700 dark:text-gray-300">{fieldLabel(v2Field)}</td>
                          <td className="py-2 pr-4 font-mono text-xs">{v1Field ?? '—'}</td>
                          <td className="py-2 pr-4 font-mono text-xs text-blue-600">{v2Field}</td>
                          <td className="py-2 pr-4 text-xs text-gray-500">{transform ?? '—'}</td>
                          <td className="py-2 text-xs text-gray-500">{required ? 'Oui' : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Requête source V1 */}
          {step.sourceQuery && (
            <Card>
              <CardHeader className="border-b border-border bg-muted/30 py-3">
                <CardTitle className="flex items-center gap-2 text-sm">
                  <Database className="h-4 w-4 text-primary" />
                  Requête source (V1)
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-3">
                <pre className="text-xs font-mono bg-gray-50 dark:bg-slate-900 rounded-lg p-3 overflow-auto max-h-72 border whitespace-pre-wrap">
                  {step.sourceQuery}
                </pre>
              </CardContent>
            </Card>
          )}

          {/* Journal d'erreur du lot */}
          {step.errorLog && (
            <Card>
              <CardHeader className="border-b border-border bg-muted/30 py-3">
                <CardTitle className="flex items-center gap-2 text-sm">
                  <AlertTriangle className="h-4 w-4 text-red-500" />
                  Journal d'erreur du lot
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-3">
                <pre className="text-xs font-mono bg-red-50 dark:bg-red-950/30 text-red-800 dark:text-red-200 rounded-lg p-3 overflow-auto max-h-48 border border-red-200 dark:border-red-900 whitespace-pre-wrap">
                  {step.errorLog}
                </pre>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Dialog d'exclusion manuelle : raison obligatoire, affichée dans le
          visualiseur et reprise telle quelle dans le rapport de migration. */}
      <Dialog
        open={!!excludeTarget}
        onOpenChange={(open) => {
          if (!open && !excluding) setExcludeTarget(null);
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ban className="h-4 w-4 text-gray-500" />
              Exclure de la migration
            </DialogTitle>
            <DialogDescription>
              {excludeTarget ? recordTitle(excludeTarget) : ''} ne sera pas repris en V2. Cet
              élément restera visible ici avec sa raison, sera listé dans le rapport de
              migration, et pourra être ré-inclus à tout moment.
            </DialogDescription>
          </DialogHeader>
          {excludeError && (
            <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-3 text-sm text-red-700 dark:text-red-300">
              {excludeError}
            </div>
          )}
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Raison de l'exclusion <span className="text-red-500">*</span>
            </label>
            <Textarea
              value={excludeReason}
              onChange={(e) => setExcludeReason(e.target.value)}
              placeholder="Ex. : donnée obsolète en V1 (classe fermée en 2024), à ne pas reprendre en V2."
              rows={3}
            />
            <p className="text-xs text-gray-500">
              Cette raison sera reprise telle quelle dans le rapport de migration — rédigez-la
              pour un lecteur non technique.
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setExcludeTarget(null)} disabled={excluding}>
              Annuler
            </Button>
            <Button
              onClick={handleExcludeConfirm}
              disabled={excluding || excludeReason.trim() === ''}
            >
              {excluding ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Ban className="h-4 w-4" />
              )}
              Exclure
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record detail dialog — aussi interface de correction/relance
          d'une ligne en échec (édition mappedData + relance unitaire). */}
      <RecordDetailDialog
        record={selectedRecord}
        fieldMapping={step.fieldMapping}
        open={!!selectedRecord}
        onClose={() => setSelectedRecord(null)}
        onRecordChanged={(updated) => {
          setSelectedRecord(updated);
          void refreshStepSilent();
        }}
      />
    </div>
  );
}
